<template>
  <v-sheet
    :class="['dashboard-devices-bar px-2 py-0', { 'is-scrollable': shouldEnableHorizontalScroll }]"
    elevation="1"
    color="white"
  >
    <v-row
      no-gutters
      :class="[
        'dashboard-devices-row flex-nowrap fill-height align-center',
        { 'is-scrollable': shouldEnableHorizontalScroll }
      ]"
    >
      <v-col
        v-for="device in projectDevicesData"
        :key="device.deviceId"
        ref="deviceCards"
        :data-device-id="device.deviceId"
        :class="['dashboard-device-col', { 'is-scrollable': shouldEnableHorizontalScroll }]"
      >
        <v-card
          class="device-card"
          :class="{ 'device-card--active': isActiveDevice(device.deviceId) }"
          :style="getCardStyle(device)"
          outlined
          role="button"
          tabindex="0"
          @click="handleCardClick(device.deviceId)"
          @keydown.enter.prevent="handleCardClick(device.deviceId)"
          @keydown.space.prevent="handleCardClick(device.deviceId)"
        >
          <div
            v-if="isActiveDevice(device.deviceId)"
            class="active-progress-fill"
            :style="{ width: autoMode ? `${progressPct}%` : '0%' }"
          ></div>

          <div
            class="color-bar"
            :style="{ backgroundColor: getLaeqColor(device.laeqNum) }"
          ></div>

          <v-card-text class="pa-1 device-card-body">
            <div class="font-weight-bold device-name text-center text-truncate mb-1">
              {{ device.name }}
            </div>

            <div class="d-flex device-card-metrics">
              <div class="flex-grow-1 text-center d-flex flex-column justify-center">
                <div class="device-metric-shell device-metric-shell--laeq">
                  <div class="label-text muted-text">Laeq</div>
                  <template v-if="isLaeqLoading(device)">
                    <div class="metric-loading-stack">
                      <v-skeleton-loader type="text" width="44" class="mx-auto skeleton-line"></v-skeleton-loader>
                      <v-skeleton-loader type="text" width="36" class="mx-auto skeleton-line"></v-skeleton-loader>
                    </div>
                  </template>
                  <template v-else>
                    <div class="value-text font-weight-bold">{{ device.laeq }}</div>
                    <div class="time-text muted-text">
                      {{ device.ts ? formatTime(device.ts) : '--' }}
                    </div>
                  </template>
                </div>
              </div>

              <v-divider vertical class="mx-1 device-divider"></v-divider>

              <div class="flex-grow-1 text-center d-flex flex-column justify-center">
                <div class="device-metric-shell device-metric-shell--average">
                  <div class="label-text muted-text average-title">
                    <span>Promedio</span>
                    <v-tooltip bottom max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          x-small
                          class="average-info-inline"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ averageTooltip(device.deviceId) }}</span>
                    </v-tooltip>
                  </div>
                  <template v-if="isAverageLoading(device)">
                    <div class="metric-loading-stack metric-loading-stack--average">
                      <v-skeleton-loader type="text" width="44" class="mx-auto skeleton-line"></v-skeleton-loader>
                      <v-skeleton-loader type="text" width="36" class="mx-auto skeleton-line"></v-skeleton-loader>
                      <v-skeleton-loader type="text" width="64" class="mx-auto skeleton-line"></v-skeleton-loader>
                    </div>
                  </template>
                  <template v-else>
                    <div class="value-text font-weight-bold">
                      {{ formatAverageValue(device) }}
                    </div>
                    <div class="time-text muted-text">
                      {{ formatAverageTime(device) }}
                    </div>
                    <div
                      class="time-text muted-text average-caption"
                      :class="{ 'average-caption--placeholder': !hasAverageValue(device) }"
                    >
                      Últimos 7 días
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DashboardDevicesBar',
  props: {
    autoMode: {
      type: Boolean,
      default: true
    },
    deviceIds: {
      type: Array,
      default: () => []
    },
    activeDeviceId: {
      type: [String, Number],
      default: null
    },
    activeProgressPct: {
      type: Number,
      default: 0
    }
  },
  data: () => ({
    laeqPolling: null,
    laeqLoadingByDeviceId: {}
  }),
  computed: {
    storeDevices() {
      return Array.isArray(this.$store.getters.devices) ? this.$store.getters.devices : []
    },
    devicesById() {
      return this.storeDevices.reduce((acc, device) => {
        acc[device.deviceId] = device
        return acc
      }, {})
    },
    orderedDevices() {
      return this.deviceIds
        .map(deviceId => this.devicesById[deviceId])
        .filter(Boolean)
    },
    averageRequestPayloads() {
      return this.orderedDevices.reduce((acc, device) => {
        const info = this.$store.getters.deviceInfo(device.deviceId)
        const sensors = Array.isArray(device.sensors) ? device.sensors : []
        const laeqSensor = sensors.find(s => s.sensorType === 'laeq')
        const referenceTs = info && info.ts && moment(info.ts).isValid() ? info.ts : null

        if (!referenceTs) return acc

        acc.push({
          deviceId: device.deviceId,
          sensorName: laeqSensor ? laeqSensor.sensorName : 'laeq_001',
          referenceTs
        })

        return acc
      }, [])
    },
    projectDevicesData() {
      return this.orderedDevices.map(device => {
        const info = this.$store.getters.deviceInfo(device.deviceId)
        const sensors = Array.isArray(device.sensors) ? device.sensors : []
        const laeqSensor = sensors.find(s => s.sensorType === 'laeq')
        const sensorName = laeqSensor ? laeqSensor.sensorName : 'laeq_001'
        const averageEntry = this.$store.getters.dashboardWeeklyAverage(device.deviceId)

        let laeqValue = '--'
        let ts = null

        if (info && Array.isArray(info.sensors)) {
          const sensorData = info.sensors.find(s => s.sensorName === sensorName)
          if (sensorData && sensorData.value !== null && sensorData.value !== undefined && !Number.isNaN(Number(sensorData.value))) {
            laeqValue = sensorData.value.toFixed(1)
          }
          ts = info.ts
        }

        return {
          deviceId: device.deviceId,
          name: device.name,
          laeq: laeqValue,
          laeqNum: laeqValue !== '--' ? parseFloat(laeqValue) : null,
          ts,
          sensorName,
          averageStatus: averageEntry ? averageEntry.status : 'idle',
          averageValue: averageEntry ? averageEntry.value : null,
          averageHourMinute: averageEntry ? averageEntry.hourMinute : null
        }
      })
    },
    averageFetchKey() {
      return this.averageRequestPayloads
        .map(device => `${device.deviceId}:${device.sensorName}:${device.referenceTs || 'none'}`)
        .join('|')
    },
    shouldEnableHorizontalScroll() {
      return this.projectDevicesData.length > 9
    },
    progressPct() {
      const value = Number(this.activeProgressPct)
      if (!Number.isFinite(value)) return 0
      return Math.min(Math.max(value, 0), 100)
    }
  },
  methods: {
    hasLaeqValue(device) {
      if (!device) return false
      return device.laeq !== '--' && device.laeq !== null && device.laeq !== undefined
    },
    isLaeqLoading(device) {
      if (!device) return false
      if (this.hasLaeqValue(device)) return false
      return !!this.laeqLoadingByDeviceId[device.deviceId]
    },
    async fetchAllLaeqs(showSkeletonIfEmpty = false) {
      if (this.orderedDevices.length === 0) return

      await Promise.all(
        this.orderedDevices.map(device => this.fetchDeviceLaeq(device.deviceId, showSkeletonIfEmpty))
      )
    },
    async fetchDeviceLaeq(deviceId, showSkeletonIfEmpty = false) {
      if (!deviceId) return null

      const info = this.$store.getters.deviceInfo(deviceId)
      const hasInfo = !!(info && info.ts && Array.isArray(info.sensors) && info.sensors.length > 0)

      if (showSkeletonIfEmpty && !hasInfo) {
        this.$set(this.laeqLoadingByDeviceId, deviceId, true)
      }

      try {
        return await this.$store.dispatch('pullDeviceLastLaeq', deviceId)
      } finally {
        this.$set(this.laeqLoadingByDeviceId, deviceId, false)
      }
    },
    async fetchAllAverages() {
      if (this.averageRequestPayloads.length === 0) return
      await this.$store.dispatch('pullDashboardWeeklyAverages', this.averageRequestPayloads)
    },
    handleCardClick(deviceId) {
      this.$emit('select-device', deviceId)
    },
    isActiveDevice(deviceId) {
      if (!this.activeDeviceId) return false
      return String(this.activeDeviceId) === String(deviceId)
    },
    getDeviceCardElement(deviceId) {
      const refs = this.$refs.deviceCards
      if (!refs) return null
      const candidates = Array.isArray(refs) ? refs : [refs]
      return candidates.find(element => {
        if (!element || !element.dataset) return false
        return String(element.dataset.deviceId) === String(deviceId)
      }) || null
    },
    scrollActiveCardIntoView(behavior = 'smooth') {
      if (!this.activeDeviceId) return
      const activeElement = this.getDeviceCardElement(this.activeDeviceId)
      if (!activeElement || typeof activeElement.scrollIntoView !== 'function') return
      activeElement.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'nearest'
      })
    },
    getContrastTextColor(hexColor) {
      if (!hexColor || typeof hexColor !== 'string') return '#111827'
      const normalized = hexColor.replace('#', '').trim()
      if (normalized.length !== 6) return '#111827'

      const red = Number.parseInt(normalized.slice(0, 2), 16)
      const green = Number.parseInt(normalized.slice(2, 4), 16)
      const blue = Number.parseInt(normalized.slice(4, 6), 16)
      if (Number.isNaN(red) || Number.isNaN(green) || Number.isNaN(blue)) return '#111827'

      const luminance = (red * 299 + green * 587 + blue * 114) / 1000
      return luminance >= 155 ? '#111827' : '#FFFFFF'
    },
    toRgba(hexColor, alpha) {
      if (!hexColor || typeof hexColor !== 'string') return `rgba(17, 24, 39, ${alpha})`
      const normalized = hexColor.replace('#', '').trim()
      if (normalized.length !== 6) return `rgba(17, 24, 39, ${alpha})`

      const red = Number.parseInt(normalized.slice(0, 2), 16)
      const green = Number.parseInt(normalized.slice(2, 4), 16)
      const blue = Number.parseInt(normalized.slice(4, 6), 16)
      if (Number.isNaN(red) || Number.isNaN(green) || Number.isNaN(blue)) return `rgba(17, 24, 39, ${alpha})`

      return `rgba(${red}, ${green}, ${blue}, ${alpha})`
    },
    getCardStyle(device) {
      if (!this.isActiveDevice(device.deviceId)) return null

      const bgColor = this.getLaeqColor(device.laeqNum)
      const textColor = this.getContrastTextColor(bgColor)
      const mutedColor = this.toRgba(textColor, 0.92)
      const dividerColor = this.toRgba(textColor, 0.32)
      const baseCardColor = this.toRgba(bgColor, 0.6)
      const progressFillColor = bgColor

      return {
        '--active-card-bg': baseCardColor,
        '--active-card-text': textColor,
        '--active-card-muted': mutedColor,
        '--active-card-divider': dividerColor,
        '--active-progress-fill': progressFillColor
      }
    },
    isAverageLoading(device) {
      return !device || device.averageStatus === 'idle' || device.averageStatus === 'loading'
    },
    hasAverageValue(device) {
      if (!device) return false
      return device.averageValue !== null && device.averageValue !== undefined && !Number.isNaN(Number(device.averageValue))
    },
    formatAverageValue(device) {
      if (!this.hasAverageValue(device)) return '--'
      return Number(device.averageValue).toFixed(1)
    },
    formatAverageTime(device) {
      if (!device || !device.averageHourMinute) return '--'
      return device.averageHourMinute
    },
    getLaeqColor(laeq) {
      if (laeq === null || laeq === undefined) return '#9E9E9E'
      if (laeq < 35) return '#A0BABF'
      if (laeq < 40) return '#A0BABF'
      if (laeq < 45) return '#B8D6D1'
      if (laeq < 50) return '#CEE4CC'
      if (laeq < 55) return '#E2F2BF'
      if (laeq < 60) return '#F3C683'
      if (laeq < 65) return '#E87E4D'
      if (laeq < 70) return '#CD463E'
      if (laeq < 75) return '#A11A4D'
      if (laeq < 80) return '#75085C'
      return '#430A4A'
    },
    averageTooltip(deviceId) {
      const average = this.$store.getters.dashboardWeeklyAverage(deviceId)
      if (!average || !average.hourMinute) {
        return 'Se calcula tomando los valores LAeq de la misma hora:minuto en cada uno de los últimos 7 días, y promediándolos en un único valor.'
      }
      return `Se calcula tomando todos los valores LAeq medidos a las ${average.hourMinute} de cada uno de los últimos 7 días, y promediándolos en un único valor para esa hora:minuto.`
    },
    formatTime(ts) {
      if (!ts) return ''
      return moment(ts).format('HH:mm')
    }
  },
  watch: {
    averageFetchKey: {
      immediate: true,
      handler() {
        this.fetchAllAverages()
      }
    },
    deviceIds: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchAllLaeqs(true)
        this.$nextTick(() => {
          this.scrollActiveCardIntoView('auto')
        })
      }
    },
    activeDeviceId() {
      this.$nextTick(() => {
        this.scrollActiveCardIntoView('smooth')
      })
    }
  },
  mounted() {
    this.laeqPolling = setInterval(() => {
      this.fetchAllLaeqs(false)
    }, 1 * 60 * 1000)
    this.$nextTick(() => {
      this.scrollActiveCardIntoView('auto')
    })
  },
  beforeDestroy() {
    if (this.laeqPolling) clearInterval(this.laeqPolling)
  }
}
</script>

<style scoped>
.dashboard-devices-bar {
  --bar-gap: 6px;
  --bar-pad-x: 8px;
  --desktop-card-width: calc((100% - (8 * var(--bar-gap))) / 9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
  padding-left: var(--bar-pad-x) !important;
  padding-right: var(--bar-pad-x) !important;
}

.dashboard-devices-row {
  display: flex;
  width: 100%;
  min-width: 100%;
  gap: var(--bar-gap);
  flex-wrap: nowrap !important;
  align-items: stretch;
}

.dashboard-device-col {
  flex: 1 1 0;
  min-width: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

.dashboard-devices-bar.is-scrollable {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scroll-snap-type: x mandatory;
  scroll-padding-left: var(--bar-pad-x);
}

.dashboard-devices-bar.is-scrollable::-webkit-scrollbar {
  height: 8px;
}

.dashboard-devices-bar.is-scrollable::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.08);
  border-radius: 99px;
}

.dashboard-devices-bar.is-scrollable::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.26);
  border-radius: 99px;
}

.dashboard-devices-row.is-scrollable {
  width: max-content;
}

.dashboard-device-col.is-scrollable {
  flex: 0 0 max(var(--desktop-card-width), 152px);
  max-width: max(var(--desktop-card-width), 152px);
  min-width: 152px;
}

.device-card {
  border-radius: 6px;
  background-color: #fff;
  color: #1f2937;
  overflow: hidden;
  position: relative;
  height: 100%;
  min-height: 94px;
  transition: box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease, background-color 0.18s ease, color 0.18s ease;
  cursor: pointer;
}

.device-card:focus {
  outline: none;
}

.device-card--active {
  background-color: var(--active-card-bg, #fff);
  color: var(--active-card-text, #111827);
  border-color: var(--active-card-divider, rgba(0, 0, 0, 0.35)) !important;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.12);
  transform: translateY(-1px);
}

.active-progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  background: var(--active-progress-fill, #111827);
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
  pointer-events: none;
  z-index: 1;
  transition: width 0.16s linear;
}

.color-bar {
  height: 3px;
  width: 100%;
  position: relative;
  z-index: 2;
}

.device-name {
  font-size: 0.75rem;
  line-height: 1.2;
}

.device-card-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.device-card-metrics {
  min-height: 56px;
}

.device-metric-shell {
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.device-metric-shell--average {
  min-height: 56px;
}

.label-text {
  font-size: 0.65rem;
  line-height: 1.1;
}

.muted-text {
  color: rgba(17, 24, 39, 0.68);
}

.value-text {
  font-size: 0.875rem;
  line-height: 1.2;
}

.time-text {
  font-size: 0.6rem;
  line-height: 1.1;
}

.average-caption {
  white-space: nowrap;
  margin: 0 auto;
}

.average-caption--placeholder {
  visibility: hidden;
}

.average-title {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.metric-loading-stack {
  min-height: 34px;
  display: grid;
  align-content: center;
  justify-items: center;
  row-gap: 2px;
}

.metric-loading-stack--average {
  min-height: 42px;
}

.skeleton-line {
  margin-bottom: 0 !important;
}

.skeleton-line >>> .v-skeleton-loader__text {
  margin: 0 !important;
}

.device-divider {
  border-color: rgba(17, 24, 39, 0.12) !important;
}

.average-info-inline {
  cursor: help;
  color: #5f9f78;
  font-size: 0.8rem !important;
  font-weight: 600;
  line-height: 1;
  vertical-align: middle;
  opacity: 0.85;
}

.average-info-inline:hover {
  opacity: 1;
}

.device-card--active .muted-text {
  color: var(--active-card-muted, rgba(255, 255, 255, 0.92)) !important;
}

.device-card--active .v-card__text {
  position: relative;
  z-index: 2;
}

.device-card--active .v-card__text,
.device-card--active .device-name,
.device-card--active .label-text,
.device-card--active .value-text,
.device-card--active .time-text,
.device-card--active .average-caption,
.device-card--active .average-title,
.device-card--active .average-title span {
  color: var(--active-card-text, #fff) !important;
}

.device-card--active .device-divider {
  border-color: var(--active-card-divider, rgba(255, 255, 255, 0.35)) !important;
}

.device-card--active .average-info-inline {
  color: var(--active-card-text, #fff);
  opacity: 0.82;
}

@media (max-width: 1264px) {
  .dashboard-devices-bar {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scroll-snap-type: x mandatory;
    scroll-padding-left: var(--bar-pad-x);
  }

  .dashboard-devices-row {
    width: max-content;
  }

  .dashboard-device-col {
    flex: 0 0 166px;
    max-width: 166px;
    min-width: 166px;
  }
}

@media (max-width: 600px) {
  .dashboard-devices-bar {
    --bar-gap: 4px;
    --bar-pad-x: 4px;
  }

  .dashboard-device-col {
    flex-basis: 150px;
    max-width: 150px;
    min-width: 150px;
  }

  .device-name {
    font-size: 0.68rem;
  }

  .label-text {
    font-size: 0.6rem;
  }

  .value-text {
    font-size: 0.8rem;
  }
}
</style>
